import { AgentCustomEvent } from '../../utils/agent-client';
import { awaitJSON, StreamingLambdaFetch } from '../../utils/fetch-utils';
import { GuardrailControl } from './governance-config';

// Live send-guard state, read from the deployed streaming service
// (GET /streaming/agentic_guardrails). This is what lets the Governance panel
// show the send locks and media cap as REAL enforced values instead of
// hardcoded constants. Read-only; returns no secrets.

export interface LiveSendGuards {
  // Master send kill-switch (AGENTIC_BUYING_SEND_ENABLED).
  sendEnabled: boolean;
  // Second, independent go-live lock (AGENTIC_BUYING_GO_LIVE_ENABLED). Both this
  // and the kill-switch must be flipped before anything agentic can reach a
  // network — two fail-closed locks, not one.
  goLiveEnabled: boolean;
  maxSendMediaUsd: number;
}

interface AgenticGuardrailsResponse {
  send?: { enabled?: boolean; goLiveEnabled?: boolean; maxMediaUsd?: number };
}

export const AGENTIC_GUARDRAILS_QUERY_KEY = 'agenticGuardrailsLive';

// The send-guard rows backed by the live endpoint, keyed by their envVar so the
// override maps onto the existing GUARDRAILS rows.
const KILL_SWITCH_ENV = 'AGENTIC_BUYING_SEND_ENABLED';
const GO_LIVE_ENV = 'AGENTIC_BUYING_GO_LIVE_ENABLED';
const SEND_CAP_ENV = 'AGENT_MAX_SEND_MEDIA_USD';

export const fetchLiveSendGuards = async (): Promise<LiveSendGuards | undefined> => {
  const res = await StreamingLambdaFetch('/agentic_guardrails');
  const body = await awaitJSON<AgenticGuardrailsResponse>(res);
  const send = body?.send;
  if (!send || typeof send.enabled !== 'boolean' || typeof send.maxMediaUsd !== 'number') {
    return undefined;
  }
  // goLiveEnabled is the newer of the two send locks. Validate it defensively: if
  // the field is missing or mistyped (e.g. an older backend), degrade fail-closed
  // — treat go-live as NOT enabled (the safe state) rather than dropping the whole
  // live read or ever claiming it is unlocked when we cannot confirm it.
  const goLiveEnabled = send.goLiveEnabled === true;
  return { sendEnabled: send.enabled, goLiveEnabled, maxSendMediaUsd: send.maxMediaUsd };
};

// The resolved DURABLE buying policy, read from the one-time `governance.policy`
// CustomEvent the agent emits at draft time (main.py → resolved_buying_policies →
// the `direct` policy). This is the honest, live counterpart to the hardcoded
// allow-list / spend-ceiling cards: the actual constraints enforced this request.
export interface LiveBuyingPolicy {
  networkWhitelist: string[];
  perIoMax: number;
  perPlanMax: number;
}

// The durable-guardrail rows backed by the governance.policy event, keyed by the
// envVar on the GUARDRAILS config rows so the overlay maps onto them.
const ALLOWLIST_ENV = 'AGENT_IO_NETWORK_WHITELIST';
const PER_IO_ENV = 'AGENT_IO_MAX';
const PER_PLAN_ENV = 'AGENT_PLAN_MAX';

const formatUsd = (n: number): string => `$${Math.round(n).toLocaleString('en-US')}`;

// Narrow the raw AG-UI custom event into the resolved direct buying policy, or
// undefined if it is not a well-formed `governance.policy` event. Mirrors
// governanceAuditFromCustomEvent — the demo owns the governance.* contract, not
// the shared agent transport. Degrades to undefined (→ config fallback) on any
// missing/mistyped field rather than showing a partial or wrong live value.
export const governancePolicyFromCustomEvent = (
  event: AgentCustomEvent
): LiveBuyingPolicy | undefined => {
  if (event.name !== 'governance.policy' || !event.value || typeof event.value !== 'object') {
    return undefined;
  }
  const { direct } = event.value as Record<string, unknown>;
  if (!direct || typeof direct !== 'object') return undefined;
  const d = direct as Record<string, unknown>;
  const whitelist = Array.isArray(d.network_whitelist)
    ? d.network_whitelist.filter((x): x is string => typeof x === 'string')
    : undefined;
  const perIoMax = typeof d.per_io_max === 'number' ? d.per_io_max : undefined;
  const perPlanMax = typeof d.per_plan_max === 'number' ? d.per_plan_max : undefined;
  if (!whitelist || whitelist.length === 0 || perIoMax === undefined || perPlanMax === undefined) {
    return undefined;
  }
  return { networkWhitelist: whitelist, perIoMax, perPlanMax };
};

// Tone describes what a guardrail's current value MEANS for send safety, so the
// badge colour is driven off the real semantics (safe vs live) and never off the
// literal ON/OFF token. Value rows that carry no safe/dangerous meaning (spend
// ceilings, the media cap, the inventory allow-list) are NEUTRAL.
export const GuardrailTone = {
  SAFE: 'safe',
  LIVE: 'live',
  NEUTRAL: 'neutral'
} as const;
export type GuardrailToneType = (typeof GuardrailTone)[keyof typeof GuardrailTone];

export interface GuardrailDisplay {
  value: string;
  isLive: boolean;
  tone: GuardrailToneType;
}

// Overlay the live send-guard state onto a guardrail row. Only the send-guard
// rows have a live source; everything else keeps its enforced-config value
// (isLive: false, NEUTRAL tone). Falls back to the config value whenever the live
// read is unavailable — so a failed/slow fetch degrades to the documented
// fail-closed default, never a lie: isLive stays false so the UI never tags it as
// a confirmed live reading.
//
// The two send locks are fail-closed. We report the STATE, not the switch
// position: the SAFE state is "engaged / blocked / locked", the DANGEROUS state
// is "live / unlocked", and colour follows that meaning (green=safe, danger=live).
export const applyLiveGuards = (
  guardrail: GuardrailControl,
  live: LiveSendGuards | undefined,
  policy?: LiveBuyingPolicy
): GuardrailDisplay => {
  const { envVar } = guardrail;

  // Durable rows backed by the governance.policy event. Fall back to the config
  // value (isLive: false) whenever the event has not been seen — so a demo run
  // that never streamed the policy degrades to the documented constant, never a
  // lie about being live.
  if (envVar === ALLOWLIST_ENV) {
    if (!policy) return { value: guardrail.value, isLive: false, tone: GuardrailTone.NEUTRAL };
    const n = policy.networkWhitelist.length;
    return {
      value: `${n} network${n === 1 ? '' : 's'}`,
      isLive: true,
      tone: GuardrailTone.NEUTRAL
    };
  }

  if (envVar === PER_IO_ENV) {
    if (!policy) return { value: guardrail.value, isLive: false, tone: GuardrailTone.NEUTRAL };
    return { value: formatUsd(policy.perIoMax), isLive: true, tone: GuardrailTone.NEUTRAL };
  }

  if (envVar === PER_PLAN_ENV) {
    if (!policy) return { value: guardrail.value, isLive: false, tone: GuardrailTone.NEUTRAL };
    return { value: formatUsd(policy.perPlanMax), isLive: true, tone: GuardrailTone.NEUTRAL };
  }

  if (envVar === KILL_SWITCH_ENV) {
    if (!live) return { value: guardrail.value, isLive: false, tone: GuardrailTone.SAFE };
    return live.sendEnabled
      ? { value: 'Agentic send → LIVE', isLive: true, tone: GuardrailTone.LIVE }
      : { value: 'Agentic send → BLOCKED (fail-closed)', isLive: true, tone: GuardrailTone.SAFE };
  }

  if (envVar === GO_LIVE_ENV) {
    if (!live) return { value: guardrail.value, isLive: false, tone: GuardrailTone.SAFE };
    return live.goLiveEnabled
      ? { value: 'Go-live → UNLOCKED', isLive: true, tone: GuardrailTone.LIVE }
      : { value: 'Go-live → LOCKED (fail-closed)', isLive: true, tone: GuardrailTone.SAFE };
  }

  if (envVar === SEND_CAP_ENV) {
    if (!live) return { value: guardrail.value, isLive: false, tone: GuardrailTone.NEUTRAL };
    return { value: `$${live.maxSendMediaUsd}`, isLive: true, tone: GuardrailTone.NEUTRAL };
  }

  return { value: guardrail.value, isLive: false, tone: GuardrailTone.NEUTRAL };
};
